import { useState, type ChangeEvent } from 'react';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { useActiveTenantAccess } from '@/hooks/use-active-tenant';
import { getErrorMessage } from '@/lib/api-error';
import { publicUrl } from '@/lib/env';
import { prepararFotoProducto } from '@/lib/image-file';
import { photoLabel } from './products-format';
import {
  PRODUCT_LIMITS,
  PRODUCT_PHOTO_MAX_BYTES,
  validatePhotoCount,
  validatePhotoFile,
} from './products-validation';
import { useUploadProductPhoto } from './use-products';

/**
 * Fotos del producto (`fotos` del catálogo): subir archivos desde el computador del admin o pegar la
 * dirección de una imagen que ya está publicada.
 *
 * La subida es un endpoint aparte (`POST .../products/fotos`, ver `useUploadProductPhoto`) que
 * devuelve la URL de la foto ya guardada en el backend; esa URL es lo que se agrega a `fotos`. Por
 * eso el botón de guardar del producto es el único que persiste el catálogo: si se cancela el
 * diálogo, las fotos subidas quedan en disco sin usar (no hay limpieza de huérfanos).
 *
 * Se sube UNA foto por archivo elegido (`multiple` en el selector hace el trabajo en serie) y se
 * valida ANTES de leer (tamaño y MIME declarado): un archivo de 20 MB no se lee para nada y el
 * usuario ve el motivo al instante. El formato real lo confirma el backend por firma binaria.
 *
 * Antes de viajar, cada foto se REESCALA en el navegador (`prepararFotoProducto`, máx. 1600 px y
 * calidad 0.82): es lo que hace que un catálogo de 100 productos no llene el disco del servidor con
 * fotos de cámara de 4 MB, sin agregar ninguna dependencia al backend.
 */

export interface ProductPhotoFieldsProps {
  /** `fotos` del formulario (URLs ya guardadas, en orden). */
  values: string[];
  onChange: (fotos: string[]) => void;
  /** `true` mientras el producto se está guardando (bloquea subir y quitar). */
  disabled?: boolean;
}

export function ProductPhotoFields({ values, onChange, disabled = false }: ProductPhotoFieldsProps) {
  const { tenantId } = useActiveTenantAccess();
  const uploadPhoto = useUploadProductPhoto(tenantId);

  const [uploading, setUploading] = useState(false);
  /** Error de la última subida (validación del cliente o mensaje del backend). */
  const [error, setError] = useState<string | null>(null);
  /** El backend avisa con `publicUrl: false` que la foto no tiene URL alcanzable. */
  const [avisoUrlRelativa, setAvisoUrlRelativa] = useState(false);
  /** Cambiar la `key` permite volver a elegir el MISMO archivo después de quitarlo. */
  const [fileInputKey, setFileInputKey] = useState(0);
  const [url, setUrl] = useState('');

  const ocupado = disabled || uploading;
  const maxMb = Math.round(PRODUCT_PHOTO_MAX_BYTES / 1024 / 1024);

  async function handleFiles(event: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files ?? []);
    setFileInputKey((key) => key + 1);
    if (files.length === 0) return;

    const countError = validatePhotoCount(values.length, files.length);
    if (countError) {
      setError(countError);
      return;
    }

    setError(null);
    setUploading(true);

    const subidas: string[] = [];
    let fallo: string | null = null;

    for (const file of files) {
      const fileError = validatePhotoFile(file);
      if (fileError) {
        fallo = fileError;
        break;
      }

      try {
        // La foto se REESCALA acá (canvas, máx. 1600 px y tope propio de 2 MB): lo que viaja y lo que
        // queda en disco es el archivo comprimido, no el original de la cámara.
        const { base64, mimeType } = await prepararFotoProducto(file);
        const saved = await uploadPhoto.mutateAsync({
          imageBase64: base64,
          ...(mimeType ? { mimeType } : {}),
        });
        subidas.push(saved.url);
        if (!saved.publicUrl) setAvisoUrlRelativa(true);
      } catch (err) {
        fallo = getErrorMessage(err);
        break;
      }
    }

    setUploading(false);
    // Lo que sí se subió no se pierde: entra a la lista aunque una foto posterior haya fallado.
    if (subidas.length > 0) onChange([...values, ...subidas]);
    if (fallo) setError(fallo);
  }

  function addUrl() {
    const foto = url.trim();
    if (foto.length === 0) return;

    const countError = validatePhotoCount(values.length, 1);
    if (countError) {
      setError(countError);
      return;
    }

    setError(null);
    onChange([...values, foto]);
    setUrl('');
  }

  function remove(index: number) {
    setError(null);
    onChange(values.filter((_foto, position) => position !== index));
  }

  return (
    <div className="space-y-3">
      {error ? <Alert tone="danger">{error}</Alert> : null}

      {avisoUrlRelativa ? (
        <Alert tone="warning">
          La foto quedó guardada, pero su URL no es pública (falta `PUBLIC_BASE_URL` en el backend):
          la visión no podrá descargarla para reconocer el producto. Configúrala y vuelve a subir la
          foto.
        </Alert>
      ) : null}

      {values.length > 0 ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {values.map((foto, index) => (
            <li key={`${foto}-${index}`} className="space-y-2 rounded-panel border border-line p-2">
              <img
                src={publicUrl(foto)}
                alt={`Foto ${index + 1} del producto`}
                loading="lazy"
                className="h-24 w-full rounded-control bg-surface-muted object-cover"
              />
              <p className="truncate text-micro text-ink-faint" title={foto}>
                {photoLabel(foto)}
              </p>

              <Button variant="ghost" size="sm" disabled={ocupado} onClick={() => remove(index)}>
                Quitar
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-caption text-ink-faint">
          Sin fotos: el bot no podrá reconocer el producto si el cliente manda una imagen.
        </p>
      )}

      <Field
        label="Subir una foto desde tu computador"
        htmlFor="productFotosArchivo"
        hint={`JPG, PNG, WEBP, GIF o HEIC de hasta ${maxMb} MB. Se sube al elegirla y se guarda con el producto (máximo ${PRODUCT_LIMITS.fotos} fotos).`}
      >
        <Input
          key={fileInputKey}
          id="productFotosArchivo"
          name="fotosArchivo"
          type="file"
          accept="image/*"
          multiple
          disabled={ocupado}
          onChange={(event) => void handleFiles(event)}
        />
      </Field>

      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="…o pegar la dirección de una imagen (URL)"
          htmlFor="productFotoUrl"
          className="min-w-64 flex-1"
        >
          <Input
            id="productFotoUrl"
            name="fotoUrl"
            placeholder="https://…"
            value={url}
            disabled={ocupado}
            onChange={(event) => setUrl(event.target.value)}
          />
        </Field>

        <Button
          variant="ghost"
          size="sm"
          disabled={ocupado || url.trim().length === 0}
          onClick={addUrl}
        >
          Agregar foto por URL
        </Button>
      </div>

      {uploading ? (
        <p className="text-caption text-ink-muted" role="status">
          Subiendo las fotos…
        </p>
      ) : (
        <p className="text-micro text-ink-faint">
          Las fotos tienen que ser alcanzables por el servidor: la visión (el módulo que reconoce el
          producto en una foto del cliente) las descarga desde su URL.
        </p>
      )}
    </div>
  );
}
